import { useState, useEffect } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface DojoSettings {
  dojo_name: string;
  dojo_logo_url: string;
}

export const useDojoSettings = () => {
  const { profile, impersonation } = useAuth();
  const [settings, setSettings] = useState<DojoSettings>({
    dojo_name: '',
    dojo_logo_url: ''
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (profile) {
      fetchSettings();
    }
  }, [profile?.dojo_id, impersonation.active]);

  const fetchSettings = async () => {
    try {
      // Determine which dojo_id to use
      let targetDojoId: string | null = null;

      if (impersonation.active && impersonation.dojoId) {
        // Use impersonation dojo_id if active
        targetDojoId = impersonation.dojoId;
      } else if (profile?.dojo_id) {
        // Use profile dojo_id for admins and students
        targetDojoId = profile.dojo_id;
      } else if (profile?.role === 'super_user') {
        // For super users without impersonation, default to Taiga Dojo
        targetDojoId = '73b9c11e-c3d0-4c06-9de4-f990a63c3d2e';
      }

      if (!targetDojoId) {
        console.log('No dojo_id available for settings');
        setLoading(false);
        return;
      }

      // Fetch dojo-specific settings
      const { data, error } = await supabase
        .from('dojo_specific_settings')
        .select('dojo_name, logo_url')
        .eq('dojo_id', targetDojoId)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setSettings({
          dojo_name: data.dojo_name || '',
          dojo_logo_url: data.logo_url || ''
        });
      }
    } catch (error) {
      console.error('Error fetching dojo settings:', error);
    } finally {
      setLoading(false);
    }
  };

  return {
    settings,
    loading,
    refetch: fetchSettings
  };
};